import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from './contexts/CustomerAuthContext';

/**
 * TokenAuth Component
 * Handles automatic authentication using a JWT token from URL parameters
 * Used after checkout to automatically log in customers and redirect to dashboard
 */
const TokenAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyToken } = useCustomerAuth();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const authenticateWithToken = async () => {
      const token = searchParams.get('token');
      const redirect = searchParams.get('redirect') || '/dashboard';

      console.log('[TokenAuth] Token received:', token ? 'Yes' : 'No');
      console.log('[TokenAuth] Redirect URL:', redirect);

      if (!token) {
        console.error('[TokenAuth] No token provided, redirecting to login');
        setError('No authentication token provided');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        setIsProcessing(true);

        // Store token in localStorage for the auth context to use
        localStorage.setItem('customer_token', token);
        console.log('[TokenAuth] Token stored in localStorage');

        // Decode token to extract customer ID (JWT tokens have 3 parts separated by dots)
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        // Decode the payload (second part)
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('[TokenAuth] Decoded token payload:', payload);

        const customerId = payload.id || payload.customerId || payload.userId;
        if (!customerId) {
          throw new Error('Customer ID not found in token');
        }

        // Verify token with backend
        const response = await fetch('/api/customer/auth/verify-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({ token, customerId })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          console.log('[TokenAuth] Token verified successfully');
          console.log('[TokenAuth] Customer:', data.customer);

          // Token is valid, redirect to the requested page
          setTimeout(() => {
            console.log('[TokenAuth] Redirecting to:', redirect);
            navigate(redirect, { replace: true });
          }, 500);
        } else {
          console.error('[TokenAuth] Token verification failed:', data.error);
          setError(data.error || 'Invalid or expired token');

          // Clear invalid token
          localStorage.removeItem('customer_token');

          // Redirect to login with the original redirect URL
          setTimeout(() => {
            navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
          }, 2000);
        }
      } catch (error) {
        console.error('[TokenAuth] Authentication error:', error);
        setError('Authentication failed. Please try logging in manually.');

        // Clear token on error
        localStorage.removeItem('customer_token');

        // Redirect to login
        setTimeout(() => {
          navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
        }, 2000);
      } finally {
        setIsProcessing(false);
      }
    };

    authenticateWithToken();
  }, [searchParams, navigate, verifyToken]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%'
      }}>
        {isProcessing ? (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' }}>
              Authenticating...
            </h2>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
              Please wait while we log you in
            </p>
          </>
        ) : error ? (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#fee2e2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
              color: '#dc2626'
            }}>
              ✕
            </div>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' }}>
              Authentication Failed
            </h2>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
              {error}
            </p>
            <p style={{ margin: '16px 0 0', color: '#9ca3af', fontSize: '12px' }}>
              Redirecting to login page...
            </p>
          </>
        ) : (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#d1fae5',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
              color: '#059669'
            }}>
              ✓
            </div>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' }}>
              Success!
            </h2>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
              Redirecting to your dashboard...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default TokenAuth;
